import { useMemo, useState } from "preact/hooks";
import type { CrmData, Opportunity, Stage } from "../../types";
import { PIPELINE_STAGES } from "../../types";
import { canMoveStage } from "../../lib/businessRules";
import { stageTotal } from "../../lib/selectors";
import { money } from "../../lib/format";
import { OpportunityCard } from "./OpportunityCard";
import { EMPTY_FILTERS, FiltersBar, type PipelineFilters } from "./FiltersBar";
import { NewOpportunityModal } from "./NewOpportunityModal";
import { MarkWonModal, MarkLostModal } from "../opportunity/OpportunityModals";
import { ConfirmDialog } from "../ui/States";
import { EmptyState } from "../ui/States";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";

interface PendingConfirm {
  opportunity: Opportunity;
  stage: Stage;
  message: string;
}

const STAGE_DOT: Record<Stage, string> = {
  "New Lead": "var(--dot-slate)",
  Qualified: "var(--dot-blue)",
  "Proposal Sent": "var(--dot-violet)",
  Negotiation: "var(--dot-amber)",
  Won: "var(--dot-green)",
  Lost: "var(--dot-red)",
};

export function PipelineBoard({ data, now, onOpenOpportunity }: { data: CrmData; now: Date; onOpenOpportunity: (id: string) => void }) {
  const [filters, setFilters] = useState<PipelineFilters>(EMPTY_FILTERS);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<Stage | null>(null);
  const [showNewOpp, setShowNewOpp] = useState(false);
  const [pendingWon, setPendingWon] = useState<Opportunity | null>(null);
  const [pendingLost, setPendingLost] = useState<Opportunity | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

  const owners = useMemo(() => Array.from(new Set(data.opportunities.map((o) => o.salesOwner))).sort(), [data.opportunities]);

  const filtered = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const minValue = filters.minValue ? Number(filters.minValue) : 0;
    return data.opportunities.filter((o) => {
      if (filters.owner && o.salesOwner !== filters.owner) return false;
      if (filters.priority && o.priority !== filters.priority) return false;
      if (minValue && (o.value ?? 0) < minValue) return false;
      if (search) {
        const company = data.companies.find((c) => c.id === o.companyId);
        const haystack = `${o.name} ${company?.name ?? ""}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [data.opportunities, data.companies, filters]);

  const summary = useMemo(() => {
    const open = data.opportunities.filter((o) => o.stage !== "Won" && o.stage !== "Lost");
    const openValue = open.reduce((s, o) => s + (o.value ?? 0), 0);
    const weighted = open.reduce((s, o) => s + ((o.value ?? 0) * o.probability) / 100, 0);
    const won = data.opportunities.filter((o) => o.stage === "Won");
    const wonValue = won.reduce((s, o) => s + (o.finalValue ?? o.value ?? 0), 0);
    return { openCount: open.length, openValue, weighted, wonCount: won.length, wonValue };
  }, [data.opportunities]);

  function attemptMove(opportunity: Opportunity, stage: Stage) {
    if (stage === opportunity.stage) return;
    if (stage === "Won") {
      setPendingWon(opportunity);
      return;
    }
    if (stage === "Lost") {
      setPendingLost(opportunity);
      return;
    }
    const check = canMoveStage(opportunity, data.proposals, stage, now);
    if (!check.allowed) {
      toast.error(check.reason ?? "This move is not allowed.");
      return;
    }
    if (check.requiresConfirmation) {
      setPendingConfirm({ opportunity, stage, message: check.confirmMessage ?? "Are you sure?" });
      return;
    }
    store.moveStage(opportunity.id, stage);
    toast.success(`${opportunity.name} moved to ${stage}.`);
  }

  return (
    <div class="page">
      <div class="page__header">
        <div>
          <h1 class="page__title">Sales Pipeline</h1>
          <p class="page__subtitle">
            {summary.openCount} open {summary.openCount === 1 ? "opportunity" : "opportunities"} across your team
          </p>
        </div>
      </div>

      <div class="summary-bar">
        <div class="summary-chip">
          <div class="summary-chip__label">Open pipeline value</div>
          <div class="summary-chip__value">{money(summary.openValue)}</div>
          <div class="summary-chip__meta">{summary.openCount} active deals</div>
        </div>
        <div class="summary-chip">
          <div class="summary-chip__label">Weighted forecast</div>
          <div class="summary-chip__value">{money(Math.round(summary.weighted))}</div>
          <div class="summary-chip__meta">by close probability</div>
        </div>
        <div class="summary-chip">
          <div class="summary-chip__label">Won revenue</div>
          <div class="summary-chip__value">{money(summary.wonValue)}</div>
          <div class="summary-chip__meta">{summary.wonCount} closed won</div>
        </div>
      </div>

      <FiltersBar filters={filters} owners={owners} onChange={setFilters} onNewOpportunity={() => setShowNewOpp(true)} />

      <div class="board">
        {PIPELINE_STAGES.map((stage) => {
          const stageOpps = filtered.filter((o) => o.stage === stage);
          return (
            <div
              key={stage}
              class={`board__column ${dragOverStage === stage ? "board__column--drop" : ""}`}
              data-ai-id={`drop-stage-${stage}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragOverStage !== stage) setDragOverStage(stage);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverStage((s) => (s === stage ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                const id = draggingId ?? e.dataTransfer?.getData("text/plain");
                const opp = data.opportunities.find((o) => o.id === id);
                setDraggingId(null);
                setDragOverStage(null);
                if (opp) attemptMove(opp, stage);
              }}
            >
              <div class="board__column-header">
                <span class="board__column-dot" style={{ background: STAGE_DOT[stage] }} />
                <span class="board__column-title">{stage}</span>
                <span class="board__column-count">{stageOpps.length}</span>
              </div>
              <div class="board__column-total">{money(stageTotal(stage, stageOpps))}</div>
              <div class="board__column-body">
                {stageOpps.length === 0 && <p class="board__empty">Drop deals here</p>}
                {stageOpps.map((o) => (
                  <OpportunityCard
                    key={o.id}
                    opportunity={o}
                    company={data.companies.find((c) => c.id === o.companyId)}
                    activities={data.activities.filter((a) => a.opportunityId === o.id)}
                    proposals={data.proposals.filter((p) => p.opportunityId === o.id)}
                    contacts={data.contacts.filter((c) => c.companyId === o.companyId)}
                    now={now}
                    draggable
                    onDragStart={(e) => {
                      setDraggingId(o.id);
                      e.dataTransfer?.setData("text/plain", o.id);
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setDragOverStage(null);
                    }}
                    onOpen={() => onOpenOpportunity(o.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && <EmptyState title="No opportunities match these filters" description="Try clearing the search or filters above." />}

      {showNewOpp && (
        <NewOpportunityModal
          companies={data.companies}
          contacts={data.contacts}
          onClose={() => setShowNewOpp(false)}
          onCreated={(id) => {
            setShowNewOpp(false);
            onOpenOpportunity(id);
          }}
        />
      )}

      {pendingWon && <MarkWonModal opportunity={pendingWon} onClose={() => setPendingWon(null)} onDone={() => setPendingWon(null)} />}

      {pendingLost && <MarkLostModal opportunity={pendingLost} onClose={() => setPendingLost(null)} onDone={() => setPendingLost(null)} />}

      {pendingConfirm && (
        <ConfirmDialog
          title="Confirm move"
          message={pendingConfirm.message}
          confirmLabel="Move anyway"
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            store.moveStage(pendingConfirm.opportunity.id, pendingConfirm.stage);
            toast.success(`${pendingConfirm.opportunity.name} moved to ${pendingConfirm.stage}.`);
            setPendingConfirm(null);
          }}
        />
      )}
    </div>
  );
}
